import { useLazyQuery, useMutation } from '@apollo/client'
import {
    IsBestPracticeOutOfSyncForUI,
    UpdateBestBestPracticeVersion,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { UpdateBestPracticeElements } from 'api/graphQL/mutations/projectProductFamily.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import { useContentItemElements } from 'components/WorkspaceEditor/components/TreeView/hooks/useContentItemElements'
import {
    useContentItemFetcher,
    usePermissionChecks,
} from 'components/WorkspaceEditor/hooks'
import { DataNode } from 'components/WorkspaceEditor/types/types'
import { getActiveTabLabel } from 'components/WorkspaceEditor/utils'
import { getParentArticleNode } from 'components/WorkspaceEditor/utils/hoveredActionUtils'
import {
    BestPracticeDataExtended,
    Elements,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/BestPracticeSyncTypes'
import { ElementType } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { isSmartProjectCopyEnabled } from 'shared/utils/bestPracticeUtils'
import {
    useShallowSelector,
    useWorkspace2Store,
} from '../../../Workspace2Store'
import {
    BestPracticeSyncAction,
    SyncActionResult,
} from '../../smartProjectCopy/types'
import { PRODUCT_TYPE_PROPERTY_NODE_TYPE } from '../constants'
import {
    buildContentElementsForSyncQuery,
    findNodeByContentItemId,
    getContentItemOwnerId,
} from '../utils'
import {
    clearSyncResultCache,
    getCacheEntry,
    invalidateCacheEntry,
    setCacheEntry,
    SyncCacheContext,
    SYNCED_RESULT,
} from './syncResultCache'
import { BestPracticeContentItem } from './useBestPracticesData'
import {
    buildMutationInputFromFreshState,
    waitForStoreUpdate,
} from './useBestPracticesSyncHelpers'

export { clearSyncResultCache }

// Constants
// Debounce sync checks by 200ms to prevent excessive API calls when content
// is rapidly edited or multiple state updates occur in quick succession
const SYNC_CHECK_DEBOUNCE_MS = 200

interface BestPracticeData {
    id: string
    currentVersionId?: string
    isUserAddedContentBestPractice?: boolean
}

export interface UseBestPracticesSyncStatusProps {
    contentItem: BestPracticeContentItem
    projectElementNodeId?: string
    enabled?: boolean
}

export interface UseBestPracticesSyncStatusReturn {
    syncActionType: SyncActionResult
    isBPOutOfSync: boolean
    isGBPOutOfSync: boolean
    isLoading: boolean
    checkSyncStatus: () => void
    handleSync: () => void
    /** Whether SyncFromBP action is available (workspace context provided) */
    isSyncFromBPEnabled: boolean
}

/**
 * Hook to check and manage Best Practice sync status.
 * Determines if a content item's BP is out of sync and provides handlers for syncing.
 *
 * @param contentItem - The content item to check sync status for
 * @param projectElementNodeId - The project element node ID
 * @param enabled - Whether to automatically check sync status (default: true)
 *
 * @example
 * ```typescript
 * const { syncActionType, isBPOutOfSync, checkSyncStatus } = useBestPracticesSyncStatus({
 *     contentItem: node,
 *     projectElementNodeId: node.id,
 *     enabled: true
 * })
 * ```
 */
export const useBestPracticesSyncStatus = ({
    contentItem,
    projectElementNodeId,
    enabled = true,
}: UseBestPracticesSyncStatusProps): UseBestPracticesSyncStatusReturn => {
    const { projectId } = useParams<{ projectId: string }>()
    const areTrackChangesVisible = useShallowEditorActionBar(
        (state) => state.visibilitySettingValues.areTrackChangesVisible
    )
    const {
        getPropertyForSettingBestPracticesConfig,
        buildHierarchyMap,
    } = useContentItemElements()
    const {
        selectedProjectElementNode,
        activeTab,
        treeData,
        setTreeData,
        actions: { setContentIdsToReplaceOnNextFetch },
    } = useShallowSelector((state) => ({
        selectedProjectElementNode: state.selectedProjectElementNode,
        activeTab: state.activeTab,
        treeData: state.treeData,
        setTreeData: state.actions.setTreeData,
        actions: state.actions,
    }))
    const { elementItemValue } = useElementItemShallowSelector((state) => ({
        elementItemValue: state.elementItemValue,
    }))

    const permission = usePermissionChecks(treeData, selectedProjectElementNode)
    const { fetchContentItemView } = useContentItemFetcher({
        projectId: projectId!,
        treeData,
        setTreeData,
        permission,
    })
    const productFamilyId = useMemo(() => {
        return elementItemValue?.elementType === ElementType.PRODUCTTYPE
            ? elementItemValue.parentFamilyId
            : selectedProjectElementNode?.elementId
    }, [
        selectedProjectElementNode?.elementId,
        elementItemValue?.elementType,
        elementItemValue?.parentFamilyId,
    ])
    // Watch for BP sync trigger (incremented after content saves)
    const bpSyncTrigger = useWorkspace2Store((state) => state.bpSyncTrigger)
    // Compute contentItemId before useState so we can read from cache
    const contentItemId =
        contentItem.contentItemView?.id || contentItem.id || ''

    const isBestPractice =
        contentItem.isBestPractice ??
        contentItem.contentItemView?.isBestPractice ??
        false

    const isGlobalBestPractice =
        contentItem.isGlobalBestPractice ??
        contentItem.contentItemView?.isGlobalBestPractice ??
        false

    const isHidden =
        contentItem.isHidden ?? contentItem.contentItemView?.isHidden ?? false

    // Cache key identifies the content item; context validates freshness on read
    const cacheKey = `${projectId}:${contentItemId}`
    const cacheContext: SyncCacheContext = useMemo(() => ({ isHidden }), [
        isHidden,
    ])
    const cachedSyncResult = getCacheEntry(cacheKey, cacheContext)

    const [syncActionType, setSyncActionType] = useState<SyncActionResult>(
        () =>
            cachedSyncResult ?? {
                action: BestPracticeSyncAction.Null,
                currentVersionId: '',
                isUserAddedContentBestPractice: false,
                latestVersionId: '',
                message: '',
            }
    )

    // Stable refs for Apollo callbacks (avoids stale closures)
    const cacheKeyRef = useRef(cacheKey)
    cacheKeyRef.current = cacheKey
    const cacheContextRef = useRef(cacheContext)
    cacheContextRef.current = cacheContext

    // Guards against duplicate concurrent sync checks
    const syncCheckInProgressRef = useRef(false)
    // Debounce timestamp for sync checks
    const lastSyncCheckTimeRef = useRef<number>(0)
    // Prevents redundant setSyncActionType when cached action matches API response
    const lastSyncActionRef = useRef<BestPracticeSyncAction>(
        cachedSyncResult?.action ?? BestPracticeSyncAction.Null
    )
    // Distinguishes remount (same value) from real edit (incremented value)
    const lastProcessedTriggerRef = useRef(bpSyncTrigger)
    // Flags that handleSync needs BP data fetched before executing
    const pendingSyncRef = useRef(false)
    // Tracks user-initiated sync loading (not mount-triggered checks)
    const [isSyncOperationInProgress, setIsSyncOperationInProgress] = useState(
        false
    )

    useEffect(() => {
        return () => {
            syncCheckInProgressRef.current = false
            lastSyncCheckTimeRef.current = 0
            pendingSyncRef.current = false
        }
    }, [])

    /**
     * Enriches elements with user-added content BP config reference.
     * Extracted helper to keep getContentItemElements callback simple.
     */
    const enrichUserAddedContent = useCallback(
        (elements: Partial<Elements>[], node: DataNode) => {
            if (
                !elements ||
                !node?.contentItemView?.isUserAdded ||
                !isSmartProjectCopyEnabled()
            ) {
                return elements
            }

            const nodeId = node?.contentItemView.id
            const hierarchyMap = buildHierarchyMap(treeData)
            const hierarchyEntry = nodeId ? hierarchyMap.get(nodeId) : undefined
            const parentContentItemView =
                hierarchyEntry?.parent?.contentItemView

            // Get text from first element for property key derivation
            const text = (elements[0]?.text as string) || ''

            // Use getPropertyForSettingBestPracticesConfig to build the ref
            const ref = getPropertyForSettingBestPracticesConfig(
                node?.contentItemView,
                parentContentItemView,
                text
            )

            if (elements[0]) {
                elements[0].userAddedContentBP = ref
            }

            return elements
        },
        [treeData]
    )

    /**
     * Gets flat content item elements (non-user-added content).
     * Returns elements with 'elements' property ONLY (NO 'children').
     */
    const getContentItemElements = useCallback(
        (node: DataNode): Partial<Elements>[] => {
            if (!node?.contentItemView?.elements) {
                return []
            }

            const elements = buildContentElementsForSyncQuery(
                node.contentItemView.elements
            )

            // Handle user-added content for Smart Project Copy
            return enrichUserAddedContent(elements, node)
        },
        [enrichUserAddedContent]
    )

    /**
     * Gets recursive content item elements (user-added content).
     * Returns elements with 'children' property for hierarchical structure.
     */
    const getRecursiveContentItemElements = useCallback(
        (node: DataNode): any[] => {
            // Get elements from the current node
            const currentElements = getContentItemElements(node)

            // Process each element to prepare for potential children
            const hierarchicalElements: any[] = currentElements.map(
                (element: any) => {
                    return { ...element }
                }
            )

            // If node has children, process each child
            if (node.children && node.children.length > 0) {
                node.children.forEach((childNode) => {
                    const childHierarchicalElements = getRecursiveContentItemElements(
                        childNode
                    )

                    // For each parent element, attach child elements based on match
                    hierarchicalElements.forEach((parentElement) => {
                        const matched = node.contentItemView?.elements?.some(
                            (el) => el.id === parentElement.id
                        )

                        if (matched) {
                            if (!parentElement.children) {
                                parentElement.children = []
                            }

                            childHierarchicalElements.forEach((child) => {
                                parentElement.children!.push({
                                    ...child,
                                })
                            })
                        }
                    })
                })
            }

            return hierarchicalElements
        },
        [getContentItemElements]
    )

    // Mutation to check if BP is out of sync
    const [checkIsBestPracticeOutOfSyncForUI] = useMutation<
        { isBestPracticeOutOfSyncForUI: SyncActionResult },
        { input: Partial<BestPracticeDataExtended> }
    >(IsBestPracticeOutOfSyncForUI, {
        onCompleted: (data) => {
            // Reset the in-progress flag when mutation completes
            syncCheckInProgressRef.current = false
            if (data?.isBestPracticeOutOfSyncForUI) {
                const result = data.isBestPracticeOutOfSyncForUI
                const newAction = result.action

                // Update cache for this content item
                setCacheEntry(
                    cacheKeyRef.current,
                    result,
                    cacheContextRef.current
                )

                // Only update state if the sync action has actually changed
                if (newAction !== lastSyncActionRef.current) {
                    lastSyncActionRef.current = newAction
                    setSyncActionType(result)
                }
            }
        },
        onError: (error) => {
            // Reset the in-progress flag on error
            syncCheckInProgressRef.current = false

            // On error, preserve the previous sync action state
            // Keep the same state so the icon remains stable
        },
    })

    /**
     * Check if best practice is out of sync
     */
    const checkIfBestPracticeIsOutOfSync = useCallback(
        (bpData: BestPracticeData) => {
            if (!bpData) return

            // Find the node in treeData
            const node = findNodeByContentItemId(contentItemId, treeData)

            if (!node) {
                return
            }

            // Read fresh contentItemViewNodes directly from store to avoid stale closure
            // This ensures we ALWAYS get the latest data after API updates
            const freshContentItemViewNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            // Find the corresponding content item in fresh contentItemViewNodes
            const contentItemNode = freshContentItemViewNodes.find(
                (item) => item.id === contentItemId
            )

            // If we have a fresh content item node, create a temporary node with updated elements
            // Otherwise fall back to the node from treeData
            const nodeWithLatestElements = contentItemNode
                ? {
                      ...node,
                      contentItemView: {
                          ...node.contentItemView,
                          elements: contentItemNode.elements || [],
                      },
                  }
                : node

            const mutationInput: Partial<BestPracticeDataExtended> = {
                parentProjectId: projectId,
                contentItemId: contentItemId,
                id: bpData.id,
                projectElementNodeId: projectElementNodeId,
                elements: bpData.isUserAddedContentBestPractice
                    ? getRecursiveContentItemElements(nodeWithLatestElements)
                    : getContentItemElements(nodeWithLatestElements),
                currentVersionId: bpData.currentVersionId,
                isHidden: isHidden,
                isUserAddedContentBestPractice:
                    bpData.isUserAddedContentBestPractice,
            }

            checkIsBestPracticeOutOfSyncForUI({
                variables: {
                    input: mutationInput,
                },
            })
        },
        [
            contentItemId,
            treeData,
            projectId,
            projectElementNodeId,
            isHidden,
            findNodeByContentItemId,
            getRecursiveContentItemElements,
            getContentItemElements,
            checkIsBestPracticeOutOfSyncForUI,
        ]
    )

    // Query to get Best Practice data
    const [getBestPracticeData, { data: bestPracticeData }] = useLazyQuery(
        BestPracticeQuery,
        {
            fetchPolicy: 'network-only',
            onCompleted: (data) => {
                if (data?.getBestPractice) {
                    // Deferred sync: user clicked sync before data was loaded
                    if (pendingSyncRef.current) {
                        pendingSyncRef.current = false
                        syncCheckInProgressRef.current = false
                        executeSyncWithDataRef.current(data.getBestPractice)
                        return
                    }

                    checkIfBestPracticeIsOutOfSync(data.getBestPractice)
                } else {
                    pendingSyncRef.current = false
                    syncCheckInProgressRef.current = false
                }
            },
            onError: () => {
                pendingSyncRef.current = false
                syncCheckInProgressRef.current = false
            },
        }
    )

    // Reassigned every render (useRef only sets the initial value) so
    // checkSyncStatus reads fresh data without depending on `data` itself,
    // whose changing reference would otherwise recreate it each fetch cycle.
    const bestPracticeDataRef = useRef(bestPracticeData)
    bestPracticeDataRef.current = bestPracticeData

    // Mutation to update BP version with current content (CreateNewBP action)
    const [
        updateBestPracticeVersion,
        { loading: updateBPLoading },
    ] = useMutation(UpdateBestBestPracticeVersion, {
        onCompleted: () => {
            // After successful sync, optimistically set to "synced" state
            lastSyncActionRef.current = BestPracticeSyncAction.None
            setCacheEntry(
                cacheKeyRef.current,
                SYNCED_RESULT,
                cacheContextRef.current
            )
            setSyncActionType(SYNCED_RESULT)
        },
        onError: (error) => {
            // Error handled silently
        },
    })

    // Mutation to update content from BP (SyncFromBP action)
    const [
        updateContentFromBestPractice,
        { loading: syncFromBPLoading },
    ] = useMutation(UpdateBestPracticeElements(6), {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            // After successful sync, optimistically set to "synced" state
            lastSyncActionRef.current = BestPracticeSyncAction.None
            setCacheEntry(
                cacheKeyRef.current,
                SYNCED_RESULT,
                cacheContextRef.current
            )
            setSyncActionType(SYNCED_RESULT)
        },
        onError: (error) => {
            // Error handled silently - reset flags to ensure loading state is cleared
            syncCheckInProgressRef.current = false
            setIsSyncOperationInProgress(false)
        },
    })

    // Check if SyncFromBP is enabled (workspace context is provided)
    const isSyncFromBPEnabled = useMemo(() => {
        return !!productFamilyId
    }, [productFamilyId])

    // Build editing input for SyncFromBP mutation
    const buildEditingInput = useCallback(() => {
        const freshNodes = useWorkspace2Store.getState().contentItemViewNodes
        const contentItemNode = freshNodes.find(
            (node) => node.id === contentItemId
        )

        if (!contentItemNode) return null
        const parentId =
            contentItemNode.dtvContentItemParentId ||
            contentItemNode.dtvParentId
        const parentNode = freshNodes.find(
            (node) => node.id === parentId || node.dtvItemId === parentId
        )
        const isProductTypeProperty =
            contentItemNode?.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE ||
            parentNode?.type === PRODUCT_TYPE_PROPERTY_NODE_TYPE

        return {
            projectProductFamilyId: productFamilyId || '',
            contentItemId: contentItemId,
            contentItemOwnerType: isProductTypeProperty
                ? 'ProductTypeProperty'
                : parentNode?.type ?? 'ContentItem',
            elements: [], // Empty elements for SyncFromBP - server replaces with BP content
            projectId: projectId || '',
            contentItemOwnerId: getContentItemOwnerId(contentItemNode),
            nodeId: projectElementNodeId || '',
            textType: contentItemNode.textType || 'Article',
            partType: contentItemNode.partType || 'Part',
            documentTemplateViewId:
                contentItemNode.documentTemplateViewId || '',
            documentTemplateViewItemId: contentItemNode.dtvItemId || '',
            // Double fallback: dtvContentItemParentId (from content item API) or dtvParentId (from tree structure API)
            contentItemParentId:
                contentItemNode.dtvContentItemParentId ||
                contentItemNode.dtvParentId ||
                '',
            contentItemParentType: isProductTypeProperty
                ? 'ContentItem'
                : parentNode?.type ?? 'None',
            propertyKey: contentItemNode.propertyRefId,
            isFromStudioManagementModule: false,
            isMasterSpecCloneNode: false,
            selectedPillElementId: '',
            isDeletingWholeParagraph: false,
            isDeletingBaseContent: false,
        }
    }, [contentItemId, projectId, projectElementNodeId, productFamilyId])

    // Refs to stabilize callback identity (Apollo functions change on every render)
    const getBestPracticeDataRef = useRef(getBestPracticeData)
    getBestPracticeDataRef.current = getBestPracticeData
    const checkIfBestPracticeIsOutOfSyncRef = useRef(
        checkIfBestPracticeIsOutOfSync
    )
    checkIfBestPracticeIsOutOfSyncRef.current = checkIfBestPracticeIsOutOfSync

    // Check sync status
    const checkSyncStatus = useCallback(() => {
        if (
            !projectId ||
            !contentItemId ||
            (!isBestPractice && !isGlobalBestPractice)
        ) {
            return
        }

        // Prevent duplicate calls if a check is already in progress
        if (syncCheckInProgressRef.current) {
            return
        }

        // Debounce: Prevent calls that happen too rapidly
        const now = Date.now()
        if (now - lastSyncCheckTimeRef.current < SYNC_CHECK_DEBOUNCE_MS) {
            return
        }

        // Mark as in progress BEFORE starting operation
        syncCheckInProgressRef.current = true
        // Update timestamp when operation starts (not before validation)
        lastSyncCheckTimeRef.current = now

        // First get BP data if we don't have it
        const latestBestPracticeData = bestPracticeDataRef.current
        if (!latestBestPracticeData?.getBestPractice) {
            getBestPracticeDataRef.current({
                variables: {
                    parentId: contentItemId,
                    projectId: projectId,
                },
            })
        } else {
            // We have BP data, check sync status directly
            checkIfBestPracticeIsOutOfSyncRef.current(
                latestBestPracticeData.getBestPractice
            )
        }
    }, [projectId, contentItemId, isBestPractice, isGlobalBestPractice])

    // Re-fires on every mount (including article re-expand) — matches classic
    // Flicker on scroll is prevented by the cached initial state (useState initializer) + lastSyncActionRef skipping
    useEffect(() => {
        if (
            enabled &&
            (isBestPractice || isGlobalBestPractice) &&
            contentItemId
        ) {
            // Don't trigger if a sync operation is already in progress
            // This prevents duplicate calls when handleSync is running
            if (syncCheckInProgressRef.current) {
                return
            }

            // Call checkSyncStatus which will fetch BP data if needed
            // or check sync status if data already exists
            checkSyncStatus()
        }
    }, [
        contentItemId,
        enabled,
        isBestPractice,
        isGlobalBestPractice,
        isHidden,
        checkSyncStatus,
    ])

    // Re-check sync status when content is saved (bpSyncTrigger changes)
    useEffect(() => {
        let timeoutId: NodeJS.Timeout | undefined
        // Only trigger on actual changes (not initial mount when bpSyncTrigger is 0)
        if (
            bpSyncTrigger &&
            bpSyncTrigger > 0 &&
            bpSyncTrigger !== lastProcessedTriggerRef.current &&
            enabled &&
            (isBestPractice || isGlobalBestPractice)
        ) {
            lastProcessedTriggerRef.current = bpSyncTrigger
            invalidateCacheEntry(cacheKey)
            // After editing, always fetch fresh BP data and then check sync
            timeoutId = setTimeout(() => {
                // Prevent duplicate calls
                if (syncCheckInProgressRef.current) {
                    return
                }

                // Mark as in progress BEFORE starting operation
                syncCheckInProgressRef.current = true
                lastSyncCheckTimeRef.current = Date.now()

                // Always fetch fresh BP data after editing
                getBestPracticeDataRef.current({
                    variables: {
                        parentId: contentItemId,
                        projectId: projectId,
                    },
                })
            }, 300)
        }

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId)
            }
            syncCheckInProgressRef.current = false
        }
    }, [
        bpSyncTrigger,
        cacheKey,
        enabled,
        isBestPractice,
        isGlobalBestPractice,
        contentItemId,
        projectId,
    ])

    // Sync execution — called directly from handleSync or deferred via onCompleted
    const executeSyncWithData = useCallback(
        async (bpData: BestPracticeData) => {
            const action = syncActionType.action
            const node = findNodeByContentItemId(contentItemId, treeData)

            if (!node) {
                return
            }

            const isUserAddedContentBestPractice =
                bpData.isUserAddedContentBestPractice

            const bpInput: Partial<BestPracticeDataExtended> = {
                parentProjectId: projectId,
                contentItemId: contentItemId,
                id: bpData.id,
                projectElementNodeId: projectElementNodeId,
                elements: isUserAddedContentBestPractice
                    ? getRecursiveContentItemElements(node)
                    : getContentItemElements(node),
                isHidden: isHidden,
            }

            if (action === BestPracticeSyncAction.CreateNewBP) {
                updateBestPracticeVersion({
                    variables: {
                        input: bpInput,
                    },
                })
            } else if (action === BestPracticeSyncAction.SyncFromBP) {
                if (!isSyncFromBPEnabled) {
                    return
                }

                const editingInput = buildEditingInput()
                if (!editingInput) {
                    return
                }

                const freshContentItemViewNodes = useWorkspace2Store.getState()
                    .contentItemViewNodes
                const contentItemNode = freshContentItemViewNodes.find(
                    (n) => n.id === contentItemId
                )
                if (!contentItemNode) {
                    return
                }

                const parentArticleId =
                    getParentArticleNode(
                        contentItemNode,
                        freshContentItemViewNodes
                    )?.id ?? ''

                try {
                    syncCheckInProgressRef.current = true
                    lastSyncCheckTimeRef.current = Date.now()
                    setIsSyncOperationInProgress(true)

                    const clickedContentItemId = contentItemId
                    const isUserAddedContent =
                        contentItemNode.isUserAdded || false

                    await updateContentFromBestPractice({
                        variables: {
                            input: editingInput,
                            partType: String(
                                editingInput.partType
                            ).toUpperCase(),
                            projectId: projectId,
                            rootArticleContentItemIds: [parentArticleId],
                            bestPracticeInput: bpInput,
                            productFamilyId: productFamilyId,
                            isTrackChangeToggleOn:
                                areTrackChangesVisible ?? false,
                            isResetAllOptions: false,
                        },
                    })

                    const oldContentItem = useWorkspace2Store
                        .getState()
                        .contentItemViewNodes.find(
                            (item) => item.id === clickedContentItemId
                        )
                    const oldSignature =
                        oldContentItem?.elements
                            ?.map((el) => el.id)
                            .join(',') || ''

                    if (isUserAddedContent) {
                        setContentIdsToReplaceOnNextFetch?.([
                            clickedContentItemId,
                        ])
                    }

                    await fetchContentItemView(getActiveTabLabel(activeTab), [
                        parentArticleId.toString(),
                    ])

                    await waitForStoreUpdate(clickedContentItemId, oldSignature)

                    const {
                        freshNode,
                        nodeWithFreshElements,
                    } = buildMutationInputFromFreshState({
                        contentId: clickedContentItemId,
                    })

                    if (!freshNode || !nodeWithFreshElements) {
                        syncCheckInProgressRef.current = false
                        setIsSyncOperationInProgress(false)
                        return
                    }

                    const freshElements = isUserAddedContentBestPractice
                        ? getRecursiveContentItemElements(nodeWithFreshElements)
                        : getContentItemElements(nodeWithFreshElements)

                    const syncCheckInput: Partial<BestPracticeDataExtended> = {
                        parentProjectId: projectId,
                        contentItemId: clickedContentItemId,
                        id: bpData.id,
                        projectElementNodeId: projectElementNodeId,
                        elements: freshElements,
                        currentVersionId: bpData.currentVersionId,
                        isHidden: isHidden,
                        isUserAddedContentBestPractice:
                            bpData.isUserAddedContentBestPractice,
                    }

                    await checkIsBestPracticeOutOfSyncForUI({
                        variables: {
                            input: syncCheckInput,
                        },
                    })

                    syncCheckInProgressRef.current = false
                    setIsSyncOperationInProgress(false)
                } catch {
                    syncCheckInProgressRef.current = false
                    setIsSyncOperationInProgress(false)
                }
            }
        },
        [
            projectId,
            contentItemId,
            projectElementNodeId,
            syncActionType.action,
            isHidden,
            updateBestPracticeVersion,
            isSyncFromBPEnabled,
            areTrackChangesVisible,
            buildEditingInput,
            updateContentFromBestPractice,
            productFamilyId,
            fetchContentItemView,
            activeTab,
            treeData,
            getContentItemElements,
            getRecursiveContentItemElements,
            checkIsBestPracticeOutOfSyncForUI,
            setContentIdsToReplaceOnNextFetch,
        ]
    )

    const executeSyncWithDataRef = useRef(executeSyncWithData)
    executeSyncWithDataRef.current = executeSyncWithData

    const handleSync = useCallback(async () => {
        if (!projectId || !contentItemId) {
            return
        }

        const bpData: BestPracticeData | undefined =
            bestPracticeData?.getBestPractice

        if (bpData) {
            await executeSyncWithData(bpData)
        } else {
            // Data missing after remount — fetch and defer sync to onCompleted
            pendingSyncRef.current = true
            syncCheckInProgressRef.current = true
            getBestPracticeDataRef.current({
                variables: {
                    parentId: contentItemId,
                    projectId: projectId,
                },
            })
        }
    }, [
        projectId,
        contentItemId,
        bestPracticeData?.getBestPractice,
        executeSyncWithData,
    ])

    const isBPOutOfSync =
        isBestPractice &&
        (syncActionType.action === BestPracticeSyncAction.SyncFromBP ||
            syncActionType.action === BestPracticeSyncAction.CreateNewBP)

    const isGBPOutOfSync =
        isGlobalBestPractice &&
        (syncActionType.action === BestPracticeSyncAction.SyncFromBP ||
            syncActionType.action === BestPracticeSyncAction.CreateNewBP)

    return {
        syncActionType,
        isBPOutOfSync,
        isGBPOutOfSync,
        isLoading:
            updateBPLoading || syncFromBPLoading || isSyncOperationInProgress,
        checkSyncStatus,
        handleSync,
        isSyncFromBPEnabled,
    }
}
